import React, { useState, useEffect } from "react";
import {
  Button,
  Table,
  Modal,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Space,
  Form,
  Upload,
} from "antd";
import S3Image from "../../../component/S3Image";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  InboxOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { GET, POST, PUT, DELETE } from "../../../utils/apirequest";
import { API } from "../../../config/API";
import Loadingbox from "../../../component/loadingbox";
import type { ColumnsType } from "antd/es/table";
import type { RcFile, UploadFile } from "antd/es/upload/interface";
import axios from "../../../config/axiosInstance";
import { useSelector } from "react-redux";
import moment from "moment";

const { TextArea } = Input;
const { Dragger } = Upload;

interface InSnap {
  id: number;
  title: string;
  file: string;
  is_image: boolean;
  createdAt: string;
  updatedAt: string;
  attributes?: {
    title: string;
    file: string;
    is_image: boolean;
    createdAt: string;
    updatedAt: string;
  };
}

interface InSnapsTabProps {
  isActive?: boolean;
}

const InSnapsTab: React.FC<InSnapsTabProps> = ({ isActive = true }) => {
  const [inSnaps, setInSnaps] = useState<InSnap[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedInSnap, setSelectedInSnap] = useState<InSnap | null>(null);
  const [uploading, setUploading] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<any>("");
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [form] = Form.useForm();
  const User = useSelector((state: any) => state?.Auth);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // Call API when tab becomes active or pagination changes
  useEffect(() => {
    if (isActive) {
      loadInSnaps();
    }
  }, [isActive, currentPage, pageSize]);

  const loadInSnaps = async () => {
    try {
      setLoading(true);

      // Build API URL with required filters and pagination
      const params = new URLSearchParams({
        "filters[is_deleted][$eq]": "false",
        "filters[is_image][$eq]": "true",
        "filters[type][$eq]": "insnap",
        "sort[0]": "order:asc",
        "sort[1]": "createdAt:desc",
        "pagination[page]": currentPage.toString(),
        "pagination[pageSize]": pageSize.toString(),
      });

      const apiUrl = `${API.SPECIAL_PAGE_MEDIA_LIST}?${params.toString()}`;
      console.log("API URL:", apiUrl);

      const response: any = await GET(apiUrl, null, null);
      console.log("Full API Response:", response);

      if (response) {
        // Handle Strapi v4 response structure
        let inSnapsData = [];
        let totalCount = 0;

        if (response.data) {
          inSnapsData = Array.isArray(response.data)
            ? response.data
            : [response.data];

          // Extract pagination info
          if (response.meta?.pagination) {
            totalCount = response.meta.pagination.total;
            setTotal(totalCount);
          } else {
            setTotal(inSnapsData.length);
          }
        } else if (Array.isArray(response)) {
          inSnapsData = response;
          setTotal(inSnapsData.length);
        } else if (response.attributes) {
          inSnapsData = [response];
          setTotal(1);
        }

        console.log("Final in-snaps data:", inSnapsData);
        console.log("Total count:", totalCount);
        setInSnaps(inSnapsData);
      } else {
        console.log("No response received");
        setInSnaps([]);
        setTotal(0);
      }
    } catch (error: any) {
      console.error("Error loading in-snaps:", error);
      console.error("Error details:", error.response || error.message);
      message.error("Failed to load in-snaps");
      setInSnaps([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditMode(false);
    setSelectedInSnap(null);
    setSelectedImageUrl("");
    setFileList([]);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (inSnap: InSnap) => {
    setEditMode(true);
    setSelectedInSnap(inSnap);
    const inSnapData: any = inSnap.attributes || inSnap;
    // Fix image prefilling - set as object structure to match display/save expectations
    setSelectedImageUrl({ imageUrl: inSnapData.file });
    setFileList([]);
    form.setFieldsValue({
      description: inSnapData.title,
      order: inSnapData.order || 0,
    });
    setModalVisible(true);
  };

  // Upload image using the correct form-data structure as per image-upload.md
  const uploadImageToServer = async (
    file: RcFile,
    description: string
  ): Promise<string> => {
    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("image", file);

      const config = {
        method: "post",
        maxBodyLength: Infinity,
        url: API.NEW_IMAGE_UPLOAD,
        headers: {
          "Content-Type": "multipart/form-data",
        },
        data: formData,
      };

      const response = await axios.request(config);

      if (response && response.data) {
        message.success("Image uploaded successfully!");
        return response.data;
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      message.error("Failed to upload image. Please try again");
      throw error;
    } finally {
      setUploading(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (file: RcFile) => {
    // Validate file type
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("You can only upload image files!");
      return false;
    }

    // Validate file size (limit to 5MB)
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error("Image must be smaller than 5MB!");
      return false;
    }

    // Get current description from form
    const description = form.getFieldValue("description") || "New In-Snap";

    try {
      const imageUrl = await uploadImageToServer(file, description);
      setSelectedImageUrl(imageUrl);

      // Create preview for the uploaded file
      const reader = new FileReader();
      reader.onload = () => {
        setFileList([
          {
            uid: "-1",
            name: file.name,
            status: "done",
            url: reader.result as string,
          },
        ]);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      // Error message already shown in uploadImageToServer
    }

    return false; // Prevent default upload behavior
  };

  // Handle file removal
  const handleFileRemove = () => {
    setSelectedImageUrl("");
    setFileList([]);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      if (!selectedImageUrl) {
        message.error("Please select an image");
        return;
      }

      const inSnapData = {
        data: {
          title: values.description || "",
          file: selectedImageUrl?.imageUrl,
          is_image: true,
          type: "insnap",
          order: values.order || 0,
          caption: values.caption || "",
          is_deleted: false,
          created_by: User?.user?.id,
        },
      };

      let response: any;
      if (editMode && selectedInSnap) {
        response = await PUT(
          `${API.SPECIAL_PAGE_MEDIA_EDIT}${selectedInSnap.id}`,
          inSnapData
        );
      } else {
        response = await POST(API.SPECIAL_PAGE_MEDIA_ADD, inSnapData);
      }

      if (response?.data) {
        message.success(
          editMode ? "In-Snap updated successfully!" : "In-Snap added successfully!"
        );
        // Reset to first page and reload
        setCurrentPage(1);
        loadInSnaps();
        setModalVisible(false);
        form.resetFields();
        setSelectedImageUrl("");
      }
    } catch (error) {
      console.error("Error saving in-snap:", error);
      message.error("Failed to save in-snap");
    }
  };

  // Handle manual refresh
  const handleRefresh = () => {
    setCurrentPage(1);
    loadInSnaps();
  };

  // Handle pagination changes
  const handleTableChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1); // Reset to first page when page size changes
    }
  };

  const handleDelete = async (inSnapId: number) => {
    try {
      const response = await DELETE(
        `${API.SPECIAL_PAGE_MEDIA_DELETE}${inSnapId}`
      );
      if (response) {
        message.success("In-Snap deleted successfully!");
        // If current page has no items after deletion, go to previous page
        if (inSnaps.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          loadInSnaps();
        }
      }
    } catch (error) {
      console.error("Error deleting in-snap:", error);
      message.error("Failed to delete in-snap");
    }
  };

  const columns: ColumnsType<InSnap> = [
    {
      title: "Preview",
      dataIndex: "file",
      key: "preview",
      width: 100,
      render: (file: string, record: InSnap) => {
        const inSnapData = record.attributes || record;
        return (
          <S3Image
            src={inSnapData.file}
            alt={inSnapData.title}
            width={60}
            height={60}
            style={{ objectFit: "cover", borderRadius: 4 }}
            preview={{
              mask: <EyeOutlined />,
            }}
          />
        );
      },
    },
    {
      title: "Title/Description",
      dataIndex: "title",
      key: "title",
      render: (title: string, record: InSnap) => {
        const inSnapData = record.attributes || record;
        return (
          <div>
            <div style={{ fontWeight: 500 }}>
              {inSnapData.title || "N/A"}
            </div>
            <div style={{ fontSize: "12px", color: "#666" }}>Image File</div>
          </div>
        );
      },
    },

    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 200,
      render: (date: string, record: InSnap) => {
        const inSnapData = record.attributes || record;
        let _date = moment(inSnapData.createdAt).format("DD-MM-YYYY HH:MM A");
        return _date;
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record: InSnap) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this in-snap?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading) {
    return <Loadingbox />;
  }

  return (
    <div>
      <br />
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span style={{ color: "#666" }}>Total In-Snaps: {total}</span>
        <div>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add New In-Snap
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
            style={{ marginLeft: 8 }}
          >
            Refresh
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={inSnaps}
        rowKey="id"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} in-snaps`,
          onChange: handleTableChange,
          onShowSizeChange: (current, size) => handleTableChange(current, size),
          pageSizeOptions: ["5", "10", "20", "50"],
        }}
        locale={{
          emptyText: "No in-snaps found. Click 'Add New In-Snap' to get started.",
        }}
        loading={loading}
      />

      {/* Add/Edit In-Snap Modal */}
      <Modal
        title={editMode ? "Edit In-Snap" : "Add New In-Snap"}
        visible={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setSelectedImageUrl("");
          setFileList([]);
        }}
        okText="Save"
        cancelText="Cancel"
        width={600}
        confirmLoading={uploading}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item label="Image" required>
            {!selectedImageUrl ? (
              <Dragger
                name="file"
                multiple={false}
                beforeUpload={handleFileUpload}
                fileList={fileList}
                accept="image/*"
                showUploadList={false}
                style={{ minHeight: 180 }}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined style={{ fontSize: 48, color: "#1890ff" }} />
                </p>
                <p className="ant-upload-text">
                  Click or drag image file to upload
                </p>
                <p className="ant-upload-hint">
                  Support for JPG, PNG, GIF, WebP formats. Max size: 5MB
                </p>
              </Dragger>
            ) : (
              <div>
                <div style={{ marginBottom: 16 }}>
                  <S3Image
                    src={selectedImageUrl?.imageUrl}
                    alt="Selected"
                    width="100%"
                    style={{
                      maxHeight: 200,
                      objectFit: "cover",
                      borderRadius: 4,
                    }}
                  />
                </div>
                <Button onClick={handleFileRemove} type="dashed" block>
                  Remove Image
                </Button>
              </div>
            )}
          </Form.Item>

          <Form.Item
            name="description"
            label="Description (max 100 characters)"
            rules={[
              {
                max: 100,
                message: "Description cannot exceed 100 characters",
              },
            ]}
          >
            <TextArea
              rows={3}
              maxLength={100}
              placeholder="Enter in-snap description or title (max 100 characters)"
              showCount
            />
          </Form.Item>

          {/* <Form.Item
            name="order"
            label="Order"
            rules={[{ required: true, message: "Please enter order value" }]}
          >
            <InputNumber
              defaultValue={1}
              min={0}
              step={1}
              placeholder="Enter order value (supports decimals)"
              style={{ width: "100%" }}
            />
          </Form.Item> */}
        </Form>
      </Modal>
    </div>
  );
};

export default InSnapsTab;
